import { DEG2RAD, RAD2DEG } from '../utils.js';
import { CLOUDS, LIGHTING, TERRAIN } from '../core/SimConfig.js';

export default class DebugHUD {
  constructor(aircraft, renderer, weaponSystem = null, particleSystem = null, app = null) {
    this.aircraft = aircraft;
    this.renderer = renderer;
    this.weaponSystem = weaponSystem;
    this.particleSystem = particleSystem;
    this.app = app;

    const hud = document.createElement('div');
    hud.id = 'hud';

    const readouts = [
      { label: 'AIRSPEED', id: 'hud-airspeed', unit: 'kts' },
      { label: 'ALTITUDE', id: 'hud-altitude', unit: 'ft' },
      { label: 'THROTTLE', id: 'hud-throttle', unit: '%' },
      { label: 'RPM',      id: 'hud-rpm',      unit: '' },
      { label: 'AOA',      id: 'hud-aoa',      unit: '°' },
      { label: 'V/S',      id: 'hud-vs',       unit: 'ft/s' },
      { label: 'FLAPS',   id: 'hud-flaps',    unit: '' },
      { label: 'ENGINE',  id: 'hud-engine',   unit: '' },
      { label: 'WEAPON',  id: 'hud-weapon',   unit: '' },
      { label: 'AMMO',    id: 'hud-ammo',     unit: '' },
      { label: 'BULLETS', id: 'hud-bullets',  unit: '' },
    ];

    this._values = {};

    for (const { label, id, unit } of readouts) {
      const row = document.createElement('div');
      row.className = 'hud-row';

      const lbl = document.createElement('span');
      lbl.className = 'hud-label';
      lbl.textContent = label;

      const val = document.createElement('span');
      val.className = 'hud-value';
      val.id = id;

      const u = document.createElement('span');
      u.className = 'hud-unit';
      u.textContent = unit;

      row.appendChild(lbl);
      row.appendChild(val);
      row.appendChild(u);
      hud.appendChild(row);

      this._values[id] = val;
    }

    // ── Performance section ──────────────────────────────────────────────
    const perfReadouts = [
      { label: 'FPS',   id: 'hud-fps',   unit: '' },
      { label: 'DRAWS', id: 'hud-draws', unit: '' },
      { label: 'MEM',   id: 'hud-mem',   unit: 'MB' },
    ];

    const sep = document.createElement('div');
    sep.className = 'hud-separator';
    hud.appendChild(sep);

    for (const { label, id, unit } of perfReadouts) {
      const row = document.createElement('div');
      row.className = 'hud-row';

      const lbl = document.createElement('span');
      lbl.className = 'hud-label';
      lbl.textContent = label;

      const val = document.createElement('span');
      val.className = 'hud-value';
      val.id = id;

      const u = document.createElement('span');
      u.className = 'hud-unit';
      u.textContent = unit;

      row.appendChild(lbl);
      row.appendChild(val);
      row.appendChild(u);
      hud.appendChild(row);

      this._values[id] = val;
    }

    // ── Particle test buttons ─────────────────────────────────────────
    if (particleSystem) {
      const sep2 = document.createElement('div');
      sep2.className = 'hud-separator';
      hud.appendChild(sep2);

      // Alive particle count readout
      const pRow = document.createElement('div');
      pRow.className = 'hud-row';
      const pLbl = document.createElement('span');
      pLbl.className = 'hud-label';
      pLbl.textContent = 'PARTICLES';
      const pVal = document.createElement('span');
      pVal.className = 'hud-value';
      pVal.id = 'hud-particles';
      pRow.appendChild(pLbl);
      pRow.appendChild(pVal);
      pRow.appendChild(document.createElement('span')); // empty unit
      hud.appendChild(pRow);
      this._values['hud-particles'] = pVal;

      const buttons = [
        { label: 'Bullet Impact', fn: () => this._spawnAhead((x, y, z) => particleSystem.spawnBulletImpact(x, y, z)) },
        { label: 'Explosion',     fn: () => this._spawnAhead((x, y, z) => particleSystem.spawnExplosion(x, y, z)) },
        { label: 'Smoke Trail',   fn: () => this._spawnAhead((x, y, z) => {
          const v = this.aircraft.velocity;
          particleSystem.spawnSmokeTrail(x, y, z, v.x, v.y, v.z);
        }) },
        { label: 'Blast Sprite',  fn: () => this._spawnOnGroundAhead((x, y, z) => {
          app?.explosionSystem?.spawnEvent('vehicleMaverickKill', x, y, z);
          particleSystem.spawnExplosion(x, y, z, { size: 1.2, debrisCount: 0 });
        }) },
      ];

      for (const { label, fn } of buttons) {
        const btn = document.createElement('button');
        btn.className = 'hud-btn';
        btn.textContent = label;
        btn.addEventListener('click', (e) => { e.stopPropagation(); fn(); });
        hud.appendChild(btn);
      }
    }

    // ── Lighting + cloud editors ────────────────────────────────────────
    if (app) {
      this._buildLightingEditor(hud);
      this._buildTerrainEditor(hud);
      this._buildCloudEditor(hud);
    }

    // FPS tracking
    this._frameTimes = [];
    this._lastTime = performance.now();

    this._el = hud;
    // this._el.style.display = 'none';
    document.getElementById('app').appendChild(hud);
  }

  setVisible(visible) {
    this._el.style.display = visible ? '' : 'none';
    // Reset FPS tracking so the first frame after re-enabling doesn't record a
    // huge delta accumulated while the HUD was hidden (and not updating).
    if (visible) {
      this._frameTimes.length = 0;
      this._lastTime = performance.now();
    }
  }

  dispose() {
    const el = document.getElementById('hud');
    if (el) el.remove();
  }

  _spawnAhead(fn) {
    const a = this.aircraft;
    const fwd = a.forwardVector;
    fn(
      a.position.x + fwd.x * 20,
      a.position.y + fwd.y * 20,
      a.position.z + fwd.z * 20,
    );
  }

  // As _spawnAhead, but dropped onto the terrain well out in front: the sprite
  // blast is a ~30 m billboard anchored at its base, so spawning it 20 m off
  // the nose just puts the camera inside the fireball.
  _spawnOnGroundAhead(fn, dist = 250) {
    const a = this.aircraft;
    const fwd = a.forwardVector;
    const x = a.position.x + fwd.x * dist;
    const z = a.position.z + fwd.z * dist;
    fn(x, this.app?.terrainCollider?.getHeightAt(x, z) ?? a.position.y, z);
  }

  update() {
    const a = this.aircraft;
    this._values['hud-airspeed'].textContent = (a.airspeed * 1.94384).toFixed(0);
    this._values['hud-altitude'].textContent = (a.altitude * 3.28084).toFixed(0);
    this._values['hud-throttle'].textContent = (a.throttle * 100).toFixed(0);
    this._values['hud-rpm'].textContent = a.engineRPM.toFixed(0);
    this._values['hud-aoa'].textContent = (a.angleOfAttack * RAD2DEG).toFixed(1);
    this._values['hud-vs'].textContent = (a.verticalSpeed * 3.28084).toFixed(1);
    const flapLabels = ['UP', 'HALF', 'FULL'];
    this._values['hud-flaps'].textContent = flapLabels[a.flapStage];
    this._values['hud-engine'].textContent = a.engineState.status;
    this._values['hud-weapon'].textContent = this.weaponSystem ? this.weaponSystem.modeLabel : 'NONE';
    this._values['hud-ammo'].textContent = this.weaponSystem ? this.weaponSystem.ammo : '—';
    this._values['hud-bullets'].textContent = this.weaponSystem ? this.weaponSystem.aliveCount : '0';
    if (this._values['hud-particles']) {
      this._values['hud-particles'].textContent = this.particleSystem ? this.particleSystem.aliveCount : '0';
    }

    // ── Performance ────────────────────────────────────────────────────
    const now = performance.now();
    this._frameTimes.push(now - this._lastTime);
    this._lastTime = now;
    if (this._frameTimes.length > 60) this._frameTimes.shift();
    const avgMs = this._frameTimes.reduce((s, v) => s + v, 0) / this._frameTimes.length;
    this._values['hud-fps'].textContent = (1000 / avgMs).toFixed(0);

    const info = this.renderer.info;
    this._values['hud-draws'].textContent = info.render.calls;

    const mem = performance.memory;
    this._values['hud-mem'].textContent = mem ? (mem.usedJSHeapSize / 1048576).toFixed(0) : '—';
  }

  // ── Lighting editor ─────────────────────────────────────────────────────

  _buildLightingEditor(hud) {
    const sep = document.createElement('div');
    sep.className = 'hud-separator';
    hud.appendChild(sep);

    const section = document.createElement('div');
    section.className = 'hud-lighting';
    hud.appendChild(section);

    const title = document.createElement('div');
    title.className = 'hud-section-title';
    title.textContent = 'LIGHTING';
    section.appendChild(title);

    // Scenario dropdown
    const dropRow = document.createElement('div');
    dropRow.className = 'hud-row';
    const dropLbl = document.createElement('span');
    dropLbl.className = 'hud-label';
    dropLbl.textContent = 'scenario';
    const select = document.createElement('select');
    select.className = 'hud-select';
    for (const key of Object.keys(LIGHTING)) {
      const opt = document.createElement('option');
      opt.value = key;
      opt.textContent = LIGHTING[key].name || key;
      select.appendChild(opt);
    }
    select.value = this.app._activeLighting;
    const stop = (e) => e.stopPropagation();
    select.addEventListener('mousedown', stop);
    select.addEventListener('click', stop);
    select.addEventListener('change', (e) => {
      stop(e);
      this.app.applyLighting(select.value);
      this._refreshLightingControls();
    });
    dropRow.appendChild(dropLbl);
    dropRow.appendChild(select);
    section.appendChild(dropRow);
    this._lightingSelect = select;

    this._lightingControlRefreshers = [];

    const getCfg = () => LIGHTING[this.app._activeLighting];
    const reapply = () => this.app.applyLighting(this.app._activeLighting);

    this._addLightingSlider(section, 'env intensity',   'environmentIntensity', 0, 3,    0.01, getCfg, reapply);
    this._addLightingSlider(section, 'exposure',        'exposure',             0, 3,    0.01, getCfg, reapply);

    this._addLightingToggle(section, 'bloom on',        'bloom.enabled',                       getCfg, reapply);
    this._addLightingSlider(section, 'bloom strength',  'bloom.strength',       0, 3,    0.01, getCfg, reapply);
    this._addLightingSlider(section, 'bloom radius',    'bloom.radius',         0, 2,    0.01, getCfg, reapply);
    this._addLightingSlider(section, 'bloom threshold', 'bloom.threshold',      0, 1,    0.01, getCfg, reapply);

    this._addLightingToggle(section, 'contrast on',     'contrast.enabled',                    getCfg, reapply);
    this._addLightingSlider(section, 'contrast',        'contrast.contrast',    0, 3,    0.01, getCfg, reapply);

    this._addLightingToggle(section, 'sun on',          'sun.enabled',                         getCfg, reapply);
    this._addLightingColor (section, 'sun color',       'sun.color',                           getCfg, reapply);
    this._addLightingSlider(section, 'sun intensity',   'sun.intensity',        0, 5,    0.01, getCfg, reapply);
    this._addLightingSunDirection(section, getCfg, reapply);

    this._addLightingColor (section, 'amb color',       'ambient.color',                       getCfg, reapply);
    this._addLightingSlider(section, 'amb intensity',   'ambient.intensity',    0, 2,    0.01, getCfg, reapply);

    this._addLightingColor (section, 'fog color',       'fog.color',                           getCfg, reapply);

    // Export button
    const exportBtn = document.createElement('button');
    exportBtn.className = 'hud-btn';
    exportBtn.textContent = 'Copy JSON';
    exportBtn.addEventListener('click', (e) => {
      stop(e);
      this._exportLightingToClipboard(exportBtn);
    });
    section.appendChild(exportBtn);
  }

  // ── Terrain editor ──────────────────────────────────────────────────────
  //
  // Edits the active LIGHTING condition's `terrain` block — the ground's own
  // brightness/tint, independent of the scene-wide env intensity and exposure
  // above. Same wiring as the cloud editor: refreshers share the lighting
  // editor's list, and every edit routes through applyLighting →
  // Terrain.setGrading, which only writes uniforms (no shader recompile).

  _buildTerrainEditor(hud) {
    const sep = document.createElement('div');
    sep.className = 'hud-separator';
    hud.appendChild(sep);

    const section = document.createElement('div');
    section.className = 'hud-lighting';
    hud.appendChild(section);

    const title = document.createElement('div');
    title.className = 'hud-section-title';
    title.textContent = 'TERRAIN GRADING';
    section.appendChild(title);

    // Active condition's terrain block; created at the identity defaults if the
    // condition has none, so the controls always have something to bind to.
    const getCfg = () => {
      const lcfg = LIGHTING[this.app._activeLighting];
      if (!lcfg.terrain) lcfg.terrain = { ...TERRAIN.grading };
      return lcfg.terrain;
    };
    const reapply = () => this.app.applyLighting(this.app._activeLighting);

    this._addLightingSlider(section, 'brightness',    'brightness',   0, 3, 0.01, getCfg, reapply);
    this._addLightingSlider(section, 'sun boost',     'sunBoost',     0, 8, 0.01, getCfg, reapply);
    this._addLightingSlider(section, 'ambient boost', 'ambientBoost', 0, 3, 0.01, getCfg, reapply);
    this._addLightingColor (section, 'tint',          'tint',                     getCfg, reapply);
    this._addLightingSlider(section, 'tint strength', 'tintStrength', 0, 1, 0.01, getCfg, reapply);
    this._addLightingSlider(section, 'saturation',    'saturation',   0, 2, 0.01, getCfg, reapply);

    const exportBtn = document.createElement('button');
    exportBtn.className = 'hud-btn';
    exportBtn.textContent = 'Copy JSON';
    exportBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this._exportBlockToClipboard(exportBtn, 'terrain');
    });
    section.appendChild(exportBtn);
  }

  // ── Cloud editor ────────────────────────────────────────────────────────
  //
  // Edits the active LIGHTING condition's `clouds` block. Reuses the lighting
  // editor's control helpers, so its refreshers live in the same list and are
  // re-synced whenever the scenario dropdown above changes. Every edit calls
  // applyLighting → CloudLayer.configure, so the deck updates in real time.

  _buildCloudEditor(hud) {
    const sep = document.createElement('div');
    sep.className = 'hud-separator';
    hud.appendChild(sep);

    const section = document.createElement('div');
    section.className = 'hud-lighting';
    hud.appendChild(section);

    const title = document.createElement('div');
    title.className = 'hud-section-title';
    title.textContent = 'CLOUD SETTINGS';
    section.appendChild(title);

    // Active condition's clouds block; created (disabled) if the condition has
    // none so the controls always have something to bind to.
    const getCfg = () => {
      const lcfg = LIGHTING[this.app._activeLighting];
      if (!lcfg.clouds) {
        lcfg.clouds = {
          coverage: 0, density: 0.06, edgeSoftness: 1, albedo: 0xffffff,
          sunScatter: 1, ambient: 0.5, altitude: { base: 1500, top: 3000 },
        };
      }
      // The wisp keys are optional per-condition overrides that fall back to
      // CLOUDS.wisp. Materialise them at those fallbacks so the sliders show
      // what's actually rendering (the helpers read a missing key as 0).
      const cc = lcfg.clouds;
      if (cc.wispStrength == null) cc.wispStrength = 1;
      if (cc.wispScale == null) cc.wispScale = CLOUDS.wisp.scale;
      if (cc.wispDrift == null) cc.wispDrift = CLOUDS.wisp.drift;
      return cc;
    };
    // Editing here means the config values are what you want on screen, so any
    // coverage the player dialled in on the start screen is dropped first —
    // otherwise it would keep masking this editor's coverage slider.
    const reapply = () => {
      this.app._cloudCoverage = null;
      this.app.applyLighting(this.app._activeLighting);
    };

    this._addLightingSlider(section, 'coverage',      'coverage',      0,   1,    0.01,  getCfg, reapply);
    this._addLightingSlider(section, 'density',       'density',       0,   0.3,  0.005, getCfg, reapply);
    this._addLightingSlider(section, 'edge softness', 'edgeSoftness',  0,   2,    0.01,  getCfg, reapply);
    this._addLightingColor (section, 'albedo',        'albedo',                          getCfg, reapply);
    this._addLightingSlider(section, 'sun scatter',   'sunScatter',    0,   3,    0.01,  getCfg, reapply);
    this._addLightingSlider(section, 'ambient',       'ambient',       0,   2,    0.01,  getCfg, reapply);
    this._addLightingSlider(section, 'noise scale',   'noiseScale',    200, 8000, 50,    getCfg, reapply);
    this._addLightingSlider(section, 'wisp strength', 'wispStrength',  0,   2,    0.01,  getCfg, reapply);
    this._addLightingSlider(section, 'wisp scale',    'wispScale',     40,  1200, 10,    getCfg, reapply);
    this._addLightingSlider(section, 'wisp drift',    'wispDrift',     0,   8,    0.05,  getCfg, reapply);
    this._addLightingSlider(section, 'alt base',      'altitude.base', 0,   6000, 10,    getCfg, reapply);
    this._addLightingSlider(section, 'alt top',       'altitude.top',  0,   8000, 10,    getCfg, reapply);

    const exportBtn = document.createElement('button');
    exportBtn.className = 'hud-btn';
    exportBtn.textContent = 'Copy JSON';
    exportBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this._exportBlockToClipboard(exportBtn, 'clouds');
    });
    section.appendChild(exportBtn);
  }

  /** Copy one sub-block of the active lighting condition as pasteable source. */
  _exportBlockToClipboard(button, blockKey) {
    const cfg = LIGHTING[this.app._activeLighting][blockKey];
    const restore = () => { button.textContent = 'Copy JSON'; };
    if (!cfg) {
      button.textContent = `No ${blockKey}`;
      setTimeout(restore, 1500);
      return;
    }
    const text = `${blockKey}: ${this._serializeLighting(cfg, '', blockKey)},\n`;
    navigator.clipboard.writeText(text).then(() => {
      button.textContent = 'Copied!';
      setTimeout(restore, 1200);
    }).catch(() => {
      button.textContent = 'Copy failed';
      setTimeout(restore, 1500);
    });
  }

  _getPath(obj, path) {
    return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
  }

  _setPath(obj, path, value) {
    const keys = path.split('.');
    const last = keys.pop();
    let o = obj;
    for (const k of keys) {
      if (o[k] == null) o[k] = {};
      o = o[k];
    }
    o[last] = value;
  }

  _makeLightingRow(parent, label) {
    const row = document.createElement('div');
    row.className = 'hud-row';
    const lbl = document.createElement('span');
    lbl.className = 'hud-label';
    lbl.textContent = label;
    row.appendChild(lbl);
    parent.appendChild(row);
    return row;
  }

  _addLightingSlider(parent, label, path, min, max, step, getCfg, reapply) {
    const row = this._makeLightingRow(parent, label);
    const input = document.createElement('input');
    input.type = 'range';
    input.min = min;
    input.max = max;
    input.step = step;
    input.className = 'hud-slider';
    const val = document.createElement('span');
    val.className = 'hud-value';

    const refresh = () => {
      const v = Number(this._getPath(getCfg(), path)) || 0;
      input.value = v;
      val.textContent = v.toFixed(2);
    };
    refresh();

    const stop = (e) => e.stopPropagation();
    input.addEventListener('mousedown', stop);
    input.addEventListener('click', stop);
    input.addEventListener('input', (e) => {
      stop(e);
      const v = Number(input.value);
      this._setPath(getCfg(), path, v);
      val.textContent = v.toFixed(2);
      reapply();
    });

    row.appendChild(input);
    row.appendChild(val);
    this._lightingControlRefreshers.push(refresh);
  }

  _addLightingToggle(parent, label, path, getCfg, reapply) {
    const row = this._makeLightingRow(parent, label);
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.className = 'hud-check';

    const refresh = () => {
      input.checked = !!this._getPath(getCfg(), path);
    };
    refresh();

    const stop = (e) => e.stopPropagation();
    input.addEventListener('mousedown', stop);
    input.addEventListener('click', stop);
    input.addEventListener('change', (e) => {
      stop(e);
      this._setPath(getCfg(), path, input.checked);
      reapply();
    });

    row.appendChild(input);
    this._lightingControlRefreshers.push(refresh);
  }

  _addLightingColor(parent, label, path, getCfg, reapply) {
    const row = this._makeLightingRow(parent, label);
    const input = document.createElement('input');
    input.type = 'color';
    input.className = 'hud-color';

    const refresh = () => {
      const hex = (this._getPath(getCfg(), path) | 0) & 0xffffff;
      input.value = '#' + hex.toString(16).padStart(6, '0');
    };
    refresh();

    const stop = (e) => e.stopPropagation();
    input.addEventListener('mousedown', stop);
    input.addEventListener('click', stop);
    input.addEventListener('input', (e) => {
      stop(e);
      const v = parseInt(input.value.slice(1), 16);
      this._setPath(getCfg(), path, v);
      reapply();
    });

    row.appendChild(input);
    this._lightingControlRefreshers.push(refresh);
  }

  _addLightingSunDirection(parent, getCfg, reapply) {
    const azRow = this._makeLightingRow(parent, 'sun azimuth');
    const azIn = document.createElement('input');
    azIn.type = 'range';
    azIn.min = 0; azIn.max = 360; azIn.step = 1;
    azIn.className = 'hud-slider';
    const azVal = document.createElement('span');
    azVal.className = 'hud-value';
    azRow.appendChild(azIn);
    azRow.appendChild(azVal);

    const elRow = this._makeLightingRow(parent, 'sun elevation');
    const elIn = document.createElement('input');
    elIn.type = 'range';
    elIn.min = -90; elIn.max = 90; elIn.step = 1;
    elIn.className = 'hud-slider';
    const elVal = document.createElement('span');
    elVal.className = 'hud-value';
    elRow.appendChild(elIn);
    elRow.appendChild(elVal);

    const refresh = () => {
      const cfg = getCfg();
      const dir = (cfg.sun && cfg.sun.direction) || [0, -1, 0];
      const [x, y, z] = dir;
      const len = Math.hypot(x, y, z) || 1;
      const nx = x / len, ny = y / len, nz = z / len;
      const elDeg = Math.asin(Math.max(-1, Math.min(1, ny))) * RAD2DEG;
      let azDeg = Math.atan2(nx, nz) * RAD2DEG;
      if (azDeg < 0) azDeg += 360;
      azIn.value = azDeg;
      elIn.value = elDeg;
      azVal.textContent = azDeg.toFixed(0);
      elVal.textContent = elDeg.toFixed(0);
    };
    refresh();

    const apply = () => {
      const az = Number(azIn.value) * DEG2RAD;
      const el = Number(elIn.value) * DEG2RAD;
      const cosEl = Math.cos(el);
      const x = cosEl * Math.sin(az);
      const y = Math.sin(el);
      const z = cosEl * Math.cos(az);
      const cfg = getCfg();
      if (!cfg.sun) cfg.sun = { enabled: true, color: 0xffffff, intensity: 1, direction: [0, -1, 0] };
      cfg.sun.direction = [x, y, z];
      azVal.textContent = Number(azIn.value).toFixed(0);
      elVal.textContent = Number(elIn.value).toFixed(0);
      reapply();
    };

    const stop = (e) => e.stopPropagation();
    for (const inp of [azIn, elIn]) {
      inp.addEventListener('mousedown', stop);
      inp.addEventListener('click', stop);
      inp.addEventListener('input', (e) => { stop(e); apply(); });
    }
    this._lightingControlRefreshers.push(refresh);
  }

  _refreshLightingControls() {
    if (this._lightingSelect) this._lightingSelect.value = this.app._activeLighting;
    for (const fn of this._lightingControlRefreshers) fn();
  }

  _exportLightingToClipboard(button) {
    const key = this.app._activeLighting;
    const cfg = LIGHTING[key];
    const text = `${key}: ${this._serializeLighting(cfg, '', '')},\n`;
    const restore = () => { button.textContent = 'Copy JSON'; };
    navigator.clipboard.writeText(text).then(() => {
      button.textContent = 'Copied!';
      setTimeout(restore, 1200);
    }).catch(() => {
      button.textContent = 'Copy failed';
      setTimeout(restore, 1500);
    });
  }

  _serializeLighting(value, indent, path) {
    if (value === null || value === undefined) return String(value);
    if (typeof value === 'boolean') return String(value);
    if (typeof value === 'string') return `'${value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
    if (typeof value === 'number') {
      if (this._isColorPath(path)) {
        const hex = (value | 0) & 0xffffff;
        return '0x' + hex.toString(16).padStart(6, '0');
      }
      return this._formatNumber(value);
    }
    if (Array.isArray(value)) {
      const parts = value.map((v) => this._serializeLighting(v, indent, path));
      return '[' + parts.join(', ') + ']';
    }
    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) return '{}';
      const entries = keys.map((k) => {
        const childPath = path ? `${path}.${k}` : k;
        return `${k}: ${this._serializeLighting(value[k], indent + '  ', childPath)}`;
      });
      const allLeaf = keys.every((k) => {
        const v = value[k];
        return v === null || typeof v !== 'object' || Array.isArray(v);
      });
      if (allLeaf && entries.join(', ').length < 100) {
        return `{ ${entries.join(', ')} }`;
      }
      return '{\n' + entries.map((s) => indent + '  ' + s + ',').join('\n') + '\n' + indent + '}';
    }
    return String(value);
  }

  _isColorPath(path) {
    return path === 'color' || path.endsWith('.color')
        || path === 'albedo' || path.endsWith('.albedo')
        || path === 'tint' || path.endsWith('.tint');
  }

  _formatNumber(n) {
    if (Number.isInteger(n)) return String(n);
    // Trim trailing zeros from a 4-decimal representation.
    return parseFloat(n.toFixed(4)).toString();
  }
}
